import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Target, Globe, Users, TrendingUp, Building } from 'lucide-react';

const stats = [
  { value: '48h', label: 'Tempo di risposta', description: 'Dalla richiesta alla presentazione' },
  { value: '100+', label: 'Profili attivi', description: 'Figure tecniche e manageriali' },
  { value: '30+', label: 'Aziende partner', description: 'Network di imprese qualificate' },
  { value: '5', label: 'Paesi target', description: 'Espansione europea in corso' },
  { value: '3', label: 'Revenue streams', description: 'Modello di business diversificato' }
];

export default function ChiSiamoPage() {
  return (
    <div data-testid="chi-siamo-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="hero-dark py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="accent-line mb-8"></div>
            <h1 data-testid="chi-siamo-headline" className="text-4xl md:text-6xl font-extrabold text-white mb-6">
              Chi Siamo
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl">
              SELL&REC è un brand di Sell&More Srl, specializzato nell'intermediazione 
              per il settore EPC e industriale.
            </p>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-20 md:py-32 bg-[#F5F5F7] noise-bg relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
              <div>
                <Target className="w-16 h-16 text-[#E94560] mb-6" strokeWidth={1.5} />
                <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-6">
                  La Nostra Missione
                </h2>
                <div className="space-y-4 text-[#555555]">
                  <p>
                    SELL&REC nasce con l'obiettivo di semplificare l'incontro tra domanda e offerta 
                    nel settore EPC e industriale. Siamo il ponte tra aziende che cercano competenze 
                    tecniche e professionisti che cercano opportunità.
                  </p>
                  <p>
                    Il nostro approccio si basa su tre pilastri fondamentali: <strong>velocità</strong>, 
                    <strong>qualità</strong> e <strong>flessibilità</strong>. Garantiamo risposte in 48 ore, 
                    profili certificati e soluzioni personalizzate.
                  </p>
                  <p>
                    Non siamo un'agenzia tradizionale: siamo partner strategici che comprendono 
                    le dinamiche del settore e sanno rispondere con rapidità alle esigenze del mercato.
                  </p>
                </div>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1689264337792-40eb98d9e740?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwc3Vuc2V0fGVufDB8fHx8MTc3MjIwMjgwMnww&ixlib=rb-4.1.0&q=85&w=600"
                  alt="Construction site sunset"
                  className="shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
              <div className="md:order-2">
                <Globe className="w-16 h-16 text-[#27AE60] mb-6" strokeWidth={1.5} />
                <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-6">
                  La Nostra Visione
                </h2>
                <div className="space-y-4 text-[#555555]">
                  <p>
                    Guardiamo all'Europa come il nostro mercato naturale. L'espansione 
                    internazionale è parte del nostro DNA: vogliamo portare il modello 
                    SELL&REC in tutti i principali hub industriali del continente.
                  </p>
                  <p>
                    La nostra visione è diventare il punto di riferimento per l'intermediazione 
                    tecnica nel settore EPC, costruendo un network paneuropeo di professionisti 
                    e imprese qualificate.
                  </p>
                </div>
              </div>
              <div className="md:order-1">
                <div className="bg-[#1A1A2E] p-10">
                  <h3 className="text-2xl font-bold text-white mb-6">I nostri valori</h3>
                  <ul className="space-y-4">
                    {['Velocità di risposta', 'Qualità certificata', 'Trasparenza totale', 'Partnership durature'].map((value, i) => (
                      <li key={i} className="flex items-center gap-3 text-gray-300">
                        <span className="w-2 h-2 bg-[#E94560]"></span>
                        {value}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 md:py-32 bg-[#1A1A2E]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-white">I numeri di Sell&Rec</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
              {stats.map((stat, i) => (
                <div key={i} data-testid={`about-stat-${i}`} className="text-center">
                  <div className="text-4xl md:text-5xl font-extrabold text-[#E94560] mb-2">{stat.value}</div>
                  <div className="text-white font-bold mb-1">{stat.label}</div>
                  <div className="text-gray-500 text-sm">{stat.description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
